// src/components/ProductsPicker.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";
import { naturalCodeCompare } from "../utils/naturalCode";

export type ProductLite = {
  code: string;
  name: string;
  price: number;
  unit?: string;
  imageUrl?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onPick: (p: ProductLite) => void;
  // (ออปชัน) ให้รีเฟรชจากหน้าพ่อได้
  initialQuery?: string;
};

export default function ProductsPicker({
  isOpen,
  onClose,
  onPick,
  initialQuery = "",
}: Props): JSX.Element | null {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState<string>(initialQuery);
  const [items, setItems] = useState<ProductLite[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const resp = await fetch(apiUrl("/products"));
        const json = await resp.json();
        if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

        const list: ProductLite[] = (json.items ?? []).map((r: any) => ({
          code: r.code,
          name: r.name,
          price: Number(r.price ?? 0),
          unit: r.unit ?? undefined,
          imageUrl: r.imageUrl ?? undefined,
        }));
        if (!alive) return;
        setItems(list.sort(naturalCodeCompare));
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? "ดึงรายการสินค้าไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.unit?.toLowerCase() ?? "").includes(q)
    );
  }, [query, items]);

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      role="dialog"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
      aria-modal="true"
      aria-labelledby="productsPickerLabel"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header">
            <h5 className="modal-title" id="productsPickerLabel">รายการสินค้าทั้งหมด</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="input-group mb-3">
              <span className="input-group-text">ค้นหา</span>
              <input
                type="text"
                className="form-control"
                placeholder="พิมพ์โค้ด / ชื่อ / หน่วย"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>

            {err && <div className="alert alert-warning rounded-3">โหลดสินค้าไม่สำเร็จ: {err}</div>}
            {loading && (
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="spinner-border spinner-border-sm" role="status" />
                <span>กำลังโหลดรายการสินค้า…</span>
              </div>
            )}

            <div className="table-responsive" style={{ maxHeight: 420 }}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: 72 }}>รูป</th>
                    <th style={{ width: 140 }}>โค้ด</th>
                    <th>ชื่อสินค้า</th>
                    <th style={{ width: 120 }} className="text-end">ราคา</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr
                      key={p.code}
                      role="button"
                      onClick={() => onPick(p)}
                      className="user-select-none"
                    >
                      <td>
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            width={56}
                            height={56}
                            style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              img.onerror = null;
                              img.src = "/no-image.png";
                            }}
                          />
                        ) : (
                          <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                        )}
                      </td>
                      <td className="fw-semibold">{p.code}</td>
                      <td>{p.name}</td>
                      <td className="text-end">
                        {p.price.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>ปิดหน้าต่าง</button>
          </div>
        </div>
      </div>
    </div>
  );
}
